// src/review/canvas/sections/AttackSurfaceSection.jsx
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Card, Stack, Text, Table } from '@mantine/core';
import { useDispatch, useSelector } from 'react-redux';
import { useReview } from '../../context/ReviewContext';
import { persistScenarioAttackSurface } from '../../../redux/slices/securitySlice';
import EditableSelectField from '../shared/EditableSelectField';

/**
 * AttackSurfaceSection - Shows attack surface assessment with editable ratings
 * Displays vulnerability and process strength ratings per attack type
 */
function AttackSurfaceSection({ threat }) {
  const dispatch = useDispatch();
  const { modelId, assessmentUuid } = useReview();

  // Get template for this assessment to extract dropdown options
  const templateData = useSelector((state) => {
    const assessment = state.assessments?.byId?.[assessmentUuid];
    const templateId = assessment?.template_id;
    return templateId ? state.templates?.byId?.[templateId] : null;
  });

  // Extract vulnerability and process options from template
  const vulnerabilityOptions = useMemo(() => {
    if (!templateData?.taxonomy?.securityTaxonomy?.security) {
      return ['Low', 'Medium', 'High']; // Fallback
    }
    const attackSurface = templateData.taxonomy.securityTaxonomy.security.find(
      (cat) => cat.name?.includes('Attack Surface')
    );
    return attackSurface?.schema?.vulnerabilityLevels || ['Low', 'Medium', 'High'];
  }, [templateData]);

  const processStrengthOptions = useMemo(() => {
    if (!templateData?.taxonomy?.securityTaxonomy?.security) {
      return ['Weak', 'Fair', 'Strong']; // Fallback
    }
    const attackSurface = templateData.taxonomy.securityTaxonomy.security.find(
      (cat) => cat.name?.includes('Attack Surface')
    );
    return attackSurface?.schema?.processStrength || ['Weak', 'Fair', 'Strong'];
  }, [templateData]);

  const attackSurface = useMemo(() => threat.attackSurface || {}, [threat.attackSurface]);
  const attackTypes = Object.keys(attackSurface);

  // Color functions for vulnerability and process ratings
  const getVulnerabilityColor = (value) => {
    switch (value) {
      case 'High':
        return 'red';
      case 'Medium':
        return 'orange';
      case 'Low':
        return 'green';
      default:
        return 'gray';
    }
  };

  const getProcessColor = (value) => {
    switch (value) {
      case 'Strong':
        return 'green';
      case 'Fair':
        return 'orange';
      case 'Weak':
        return 'red';
      default:
        return 'gray';
    }
  };

  // Handler for saving attack surface ratings (per attack type, per field)
  const handleSaveAttackSurface = useCallback(
    (attackType, field) => async (newValue, oldValue) => {
      const currentRow = attackSurface[attackType] || {};
      const updatedRow = { ...currentRow, [field]: newValue };

      await dispatch(
        persistScenarioAttackSurface({
          modelId,
          assessmentUuid,
          scenarioId: threat.scenarioId || threat.threatId,
          attackType,
          rowData: updatedRow,
        })
      );
    },
    [dispatch, modelId, assessmentUuid, threat.scenarioId, threat.threatId, attackSurface]
  );

  // If no attack surface data, show placeholder
  if (attackTypes.length === 0) {
    return (
      <Card withBorder p="md">
        <Stack gap="md">
          <Text size="md" fw={700}>
            Attack Surface Assessment
          </Text>
          <Text size="sm" c="dimmed" fs="italic">
            No attack surface assessment available for this threat scenario.
          </Text>
        </Stack>
      </Card>
    );
  }

  return (
    <Card withBorder p="md">
      <Stack gap="md">
        <Text size="md" fw={700}>
          Attack Surface Assessment
        </Text>

        <Table striped highlightOnHover withTableBorder withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Attack Type</Table.Th>
              <Table.Th>Vulnerability Rating</Table.Th>
              <Table.Th>Process Strength</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {attackTypes.map((attackType) => {
              const row = attackSurface[attackType];
              return (
                <Table.Tr key={attackType}>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {attackType}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <EditableSelectField
                      label=""
                      value={row.vulnerability || ''}
                      options={vulnerabilityOptions}
                      itemType="threats"
                      itemId={threat.threatId}
                      fieldName={`attackSurface.${attackType}.vulnerability`}
                      onSave={handleSaveAttackSurface(attackType, 'vulnerability')}
                      getColor={getVulnerabilityColor}
                      placeholder="Select rating..."
                    />
                  </Table.Td>
                  <Table.Td>
                    <EditableSelectField
                      label=""
                      value={row.process || ''}
                      options={processStrengthOptions}
                      itemType="threats"
                      itemId={threat.threatId}
                      fieldName={`attackSurface.${attackType}.process`}
                      onSave={handleSaveAttackSurface(attackType, 'process')}
                      getColor={getProcessColor}
                      placeholder="Select rating..."
                    />
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Stack>
    </Card>
  );
}

AttackSurfaceSection.propTypes = {
  threat: PropTypes.shape({
    threatId: PropTypes.string.isRequired,
    scenarioId: PropTypes.string,
    attackSurface: PropTypes.object,
  }).isRequired,
};

export default AttackSurfaceSection;
